/**
 * 钉钉 OAuth2 登录工具函数
 */

const DINGTALK_STATE_KEY = 'dingtalk_oauth_state';
const DINGTALK_RETURN_URL_PREFIX = 'dingtalk_oauth_return_';

const DINGTALK_CONFIG = {
  authUrl:
    process.env.REACT_APP_DINGTALK_AUTH_URL ||
    'https://login.dingtalk.com/oauth2/auth',
  appKey: process.env.REACT_APP_DINGTALK_APP_KEY || '',
  apiBaseUrl: process.env.REACT_APP_API_BASE_URL || 'http://localhost:3002/api',
  scope: process.env.REACT_APP_DINGTALK_SCOPE || 'openid',
  prompt: process.env.REACT_APP_DINGTALK_PROMPT || 'consent',
};

declare global {
  interface Window {
    DTFrameLogin?: (
      frameParams: {
        id: string;
        width?: number;
        height?: number;
      },
      loginParams: {
        redirect_uri: string;
        response_type: 'code';
        client_id: string;
        scope: string;
        state: string;
        prompt: string;
      },
      successCbk: (result: { authCode?: string; code?: string; state?: string }) => void,
      errorCbk?: (errorMsg: string) => void
    ) => void;
  }
}

export interface DingtalkCallbackPayload {
  authCode?: string;
  code?: string;
  state?: string;
  error?: string;
}

export interface DingtalkCallbackResponse {
  success: boolean;
  message?: string;
  error?: string;
  code?: string;
  sessionEstablished?: boolean;
  session?: DingtalkAuthSession | null;
  user?: {
    id: string;
    name: string;
    status: string;
    matchedBy: 'dingtalk_user_id' | 'email';
  };
}

export interface DingtalkAuthSession {
  access_token: string;
  refresh_token: string;
  expires_in: number;
  expires_at?: number;
  token_type: string;
  user: {
    id: string;
    email?: string | null;
  } | null;
}

function getRedirectUri(): string {
  if (process.env.REACT_APP_DINGTALK_REDIRECT_URI) {
    return process.env.REACT_APP_DINGTALK_REDIRECT_URI;
  }

  return `${window.location.origin}/login`;
}

function generateState(): string {
  return `${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 15)}`;
}

function saveState(state: string): void {
  sessionStorage.setItem(DINGTALK_STATE_KEY, state);
}

function saveReturnUrl(state: string, returnUrl: string): void {
  sessionStorage.setItem(`${DINGTALK_RETURN_URL_PREFIX}${state}`, returnUrl);
}

export function consumeReturnUrl(state: string): string | null {
  const key = `${DINGTALK_RETURN_URL_PREFIX}${state}`;
  const returnUrl = sessionStorage.getItem(key);
  if (returnUrl) {
    sessionStorage.removeItem(key);
  }
  return returnUrl;
}

export function verifyState(state: string): boolean {
  const savedState = sessionStorage.getItem(DINGTALK_STATE_KEY);
  if (!savedState || savedState !== state) {
    return false;
  }

  sessionStorage.removeItem(DINGTALK_STATE_KEY);
  return true;
}

export function getDingtalkAuthUrl(state?: string): string {
  const finalState = state || generateState();
  saveState(finalState);

  const params = new URLSearchParams({
    redirect_uri: getRedirectUri(),
    response_type: 'code',
    client_id: DINGTALK_CONFIG.appKey,
    scope: DINGTALK_CONFIG.scope,
    state: finalState,
    prompt: DINGTALK_CONFIG.prompt,
  });

  return `${DINGTALK_CONFIG.authUrl}?${params.toString()}`;
}

export function redirectToDingtalk(returnUrl?: string): void {
  const state = generateState();
  saveState(state);

  if (returnUrl) {
    saveReturnUrl(state, returnUrl);
  }

  const authUrl = getDingtalkAuthUrl(state);
  window.location.href = authUrl;
}

export function readDingtalkCallbackPayload(): DingtalkCallbackPayload {
  const searchParams = new URLSearchParams(window.location.search);

  return {
    authCode: searchParams.get('authCode') || undefined,
    code: searchParams.get('code') || undefined,
    state: searchParams.get('state') || undefined,
    error: searchParams.get('error') || undefined,
  };
}

export function hasDingtalkCallbackPayload(payload: DingtalkCallbackPayload): boolean {
  return Boolean(payload.authCode || payload.code || payload.error);
}

export function clearDingtalkCallbackPayloadFromUrl(): void {
  const url = new URL(window.location.href);
  url.searchParams.delete('authCode');
  url.searchParams.delete('code');
  url.searchParams.delete('state');
  url.searchParams.delete('error');
  window.history.replaceState({}, document.title, url.toString());
}

export async function handleDingtalkCallback(
  payload: DingtalkCallbackPayload
): Promise<DingtalkCallbackResponse> {
  if (payload.error) {
    throw new Error(payload.error);
  }

  if (!payload.state) {
    throw new Error('缺少 state 参数');
  }

  if (!verifyState(payload.state)) {
    throw new Error('state 校验失败，请重新扫码登录');
  }

  const response = await fetch(`${DINGTALK_CONFIG.apiBaseUrl}/auth/dingtalk/callback`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      authCode: payload.authCode,
      code: payload.code,
      state: payload.state,
      error: payload.error,
    }),
  });

  const result = (await response.json()) as DingtalkCallbackResponse;

  if (!response.ok || !result.success) {
    throw new Error(result.error || result.message || '钉钉登录失败');
  }

  return result;
}

export function isRedirectUriSameOrigin(): boolean {
  const redirectUri = getRedirectUri();

  try {
    const redirectUrl = new URL(redirectUri, window.location.origin);
    return redirectUrl.origin === window.location.origin;
  } catch (_error) {
    return false;
  }
}

export function initEmbeddedDingtalkLogin(params: {
  containerId: string;
  state: string;
  onSuccess: (payload: { authCode?: string; code?: string; state?: string }) => void;
  onError?: (message: string) => void;
}): { enabled: boolean; reason?: string } {
  if (!isRedirectUriSameOrigin()) {
    return {
      enabled: false,
      reason: '当前 redirect_uri 与登录页非同源，无法启用内嵌二维码',
    };
  }

  if (typeof window.DTFrameLogin !== 'function') {
    return {
      enabled: false,
      reason: '未检测到钉钉扫码 SDK，使用跳转授权方式',
    };
  }

  try {
    window.DTFrameLogin(
      {
        id: params.containerId,
        width: 280,
        height: 280,
      },
      {
        redirect_uri: encodeURIComponent(getRedirectUri()),
        response_type: 'code',
        client_id: DINGTALK_CONFIG.appKey,
        scope: DINGTALK_CONFIG.scope,
        state: params.state,
        prompt: DINGTALK_CONFIG.prompt,
      },
      params.onSuccess,
      (errorMsg) => {
        if (params.onError) {
          params.onError(errorMsg);
        }
      }
    );

    return { enabled: true };
  } catch (error) {
    return {
      enabled: false,
      reason: error instanceof Error ? error.message : '初始化内嵌二维码失败',
    };
  }
}

export function prepareStateForEmbeddedLogin(): string {
  const state = generateState();
  saveState(state);
  return state;
}
